/**
 * Carries a partner hand-off across our own full-page redirects.
 *
 * Partners (Francoflex, Madame As) embed the app in a cross-site iframe and
 * pass the session in the query string:
 *
 *   /?source=francoflex&token=<firebase custom token>&organizationId=…&lng=fr
 *
 * Any `window.location.href = '/somewhere'` we do inside that iframe drops
 * the query string. The Firebase session is supposed to cover the gap, but it
 * lives in IndexedDB under the partner's partitioned third-party storage, so
 * it does not reliably survive the hop — the learner lands on our own
 * email/password login screen inside the partner's page.
 *
 * Keeping these params on internal redirects lets the destination re-establish
 * the session from the token instead of guessing that it is still there.
 */
export const PARTNER_SESSION_PARAMS = [
  'token',
  'source',
  'organizationId',
  'learn',
  'lng',
];

/**
 * Adds the partner hand-off params from the current URL to an internal
 * destination, without overwriting anything the destination sets itself.
 *
 * @param {string} destination e.g. '/onboarding' or '/?returnTo=onboarding'
 * @param {string} [search] defaults to the current query string
 * @returns {string} the destination with the partner params merged in
 */
export const withPartnerSession = (
  destination,
  search = typeof window !== 'undefined' ? window.location.search : ''
) => {
  const [pathAndQuery, hash] = String(destination).split('#');
  const [path, destinationQuery = ''] = pathAndQuery.split('?');

  const params = new URLSearchParams(destinationQuery);
  const current = new URLSearchParams(search);

  PARTNER_SESSION_PARAMS.forEach((name) => {
    const value = current.get(name);

    // The destination asked for this value explicitly — leave it alone.
    if (value && !params.has(name)) {
      params.set(name, value);
    }
  });

  const query = params.toString();

  return `${path}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
};
